/**
 * The assistant's /v1 contract: what the phone sends POST /agent/chat beside
 * the messages, what GET /agent/status answers, and the envelope every tool
 * answers inside the UI message stream. Phase 2 of
 * docs/plans/ai-setup-and-agent.md.
 *
 * The messages themselves are the AI SDK's `UIMessage[]` and are not declared
 * here: both sides take that type from `ai`. What IS here is everything the
 * two sides must agree on and the SDK does not know about.
 *
 * Every tool answers one of six envelopes, told apart by `kind`:
 *
 *   answer     a read. Facts for the model to put into words; the phone does
 *              not draw these.
 *   large      a read that stopped before reading, because the set is large
 *              and the call carried no window and no search: the count, the
 *              span, and the narrowings to offer. The model asks once.
 *   proposal   a write the person has not confirmed. The phone draws a card
 *              that says "not saved yet" and confirms it through the app's own
 *              route or hook; the agent itself writes nothing, ever.
 *   refused    a proposal that could not stand, with every problem at once,
 *              the same way the routes refuse.
 *   question   ask_user. The loop stops and the phone draws the choices.
 *   error      the tool itself failed. The server's safeExecute makes these; a
 *              tool never throws into the model.
 *
 * A proposal carries ids in the payload the phone posts (`values` keyed by
 * field id, `request` bodies, `recordId`s) and words beside them for the card
 * (`recordName`, `fields[].label`, `summary`). Decision 2 of the plan: the
 * confirm path is the app's own path, so every payload here is exactly what
 * the existing route or hook takes.
 */
import type { AgentLanguageTag } from './agent-language';
import type { LiveOrigin, RecordNote } from './agent-live';
import { isLiveEndReason, type LiveEndReason } from './agent-live-wire';
import type { Authority } from './auth';
import type { DocKind } from './doc-kinds';
import type { DocLayoutWire } from './doc-layout';
import type { DocTotalCause, DocTotalWord } from './document-math';
import type { DocEntryRefusal, DocGap } from './document-rules';
import type { EntryLinesWrite, LineWrite, RecordBlankSpec, RecordStructureRequest, VisibleRecordKind } from './api';
import type { ComposedSetup, ComposedUnmetNeed } from './compose';
import type { Coverage, SetupUnderstanding, Slot } from './setup-understanding';
import type { InsightAggNode, InsightCardId, InsightFilterOp, InsightRowSource, InsightWhere } from './insights';
import type { LinkCreateRequest, LinkPatchRequest } from './links';
import type { BusinessPatchRequest, GrantPatchRequest, JobCreateRequest, JobPatchRequest } from './team';
import type {
  AccessSet,
  AskAnswerRequest,
  AskCreateRequest,
  PermissionMove,
  PersonAccessPutRequest,
  PersonInviteRequest,
  PersonPatchRequest,
  PrimaryHandoverRequest,
  RecordAccessLevel,
  RoleCopyRequest,
  RoleCreateRequest,
  RolePatchRequest,
} from './people';
import type { AccessLevel, RolePermissions } from './roles';
import type { AgentView, ViewOutput } from './agent-view';
import type { FillOutput } from './agent-fill';
import type { AgentDraft } from './agent-draft';
import type { BusinessDetailValue } from './business-details';
import type { SetupLearned } from './agent-files';
import type { SetupTurnContext } from './setup-harness';

/* ------------------------------------------------------------- the routes */

/** GET /agent/status. With `configured: false` the phone hides the door. */
export type AgentStatus = {
  readonly configured: boolean;
  readonly providers: { readonly google: boolean; readonly openai: boolean };
  /**
   * Present, and true, when a stand-in answers instead of a provider: the
   * scripted development model (`AGENT_MOCK=scripted`, never in production)
   * or a test's override. Absent otherwise.
   */
  readonly mock?: boolean;
};

/**
 * POST /agent/chat body: `{ messages: UIMessage[]; context?: AgentChatContext }`.
 * The context is what the person is looking at. It only colours the prompt,
 * so the server reads it loosely and drops what it does not recognise.
 */
export type AgentChatContext = {
  readonly screen: string;
  readonly recordId?: string;
  /** The device's IANA zone, so "today" is the person's day and not the server's. */
  readonly tz?: string;
  /** ISO 3166-1 alpha-2 from the device locale. Colours a currency guess in setup. */
  readonly region?: string;
  /** ISO 4217 guessed from the device. Setup uses it unless they name another. */
  readonly localeCurrency?: string;
  /**
   * Setup mode: `screen` is `setup`, the request carries NO tenant header,
   * and this is what the person has already typed on the way in. The agent
   * composes a new business and answers a `setup` proposal; the phone posts
   * it to POST /onboarding/compose itself.
   */
  readonly draft?: {
    readonly businessName?: string;
    readonly currency?: string;
  };
  /**
   * The insight cards pinned where the person is looking: the record's own
   * strip when `recordId` is set, and the Dashboard's. A layout lives on the
   * phone and nowhere else, so this is the only way the agent can know which
   * card "the This month card" is when asked to change or take it down.
   */
  readonly cards?: readonly AgentScreenCard[];
  /**
   * Setup mode, the hand-off turn: the person tapped Build my setup. No
   * interviewer runs; the server hands `understanding` to the builder, which
   * composes the whole business and streams its progress and the proposal.
   * docs/plans/done/onboarding-two-phases.md, decision 4.
   */
  readonly build?: true;
  /**
   * Setup mode, with `build`: what the gatherer noted, as the phone holds it
   * from the newest `interview` part. The server validates it through the same
   * schema the tool takes, and the builder reads it as its brief.
   */
  readonly understanding?: SetupUnderstanding;
  /**
   * Setup mode: how the person is talking. `voice` seats the voice interviewer,
   * whose questions are spoken with their choices inside the sentence; absent
   * or `text` seats the one that draws chips. docs/plans/done/voice-setup-call.md.
   */
  readonly mode?: 'voice' | 'text';
  /**
   * The app's language. The setup interviewer speaks and writes it; inside a
   * business Coach writes it whatever is spoken (H4), and the live call's
   * voice expects it first but answers in the language it hears.
   */
  readonly language?: AgentLanguageTag;
  /**
   * Setup mode, a new workspace: the name of the one the person already
   * holds and set it up from. OS says this one starts empty and nothing from
   * that one is copied, and never asks about it (stage 7, Decided 7).
   */
  readonly beside?: string;
  /**
   * Setup mode on the web, live: the opening's three cards are on the screen
   * under OS's first line (board flow F1), so that line welcomes the person
   * and points at them rather than asking a question of its own.
   */
  readonly opening?: 'cards';
  /**
   * Setup mode: the person's setup files that are read, in the order they
   * were handed over (docs/plans/setup-from-files.md, Decided 8). The server
   * learns from their summaries once per set and tells the interviewer.
   */
  readonly fileIds?: readonly string[];
  /**
   * Setup mode only: the setup session this turn runs on. Present means the
   * turn runs on the harness, and the server reads the thread and the draft
   * from the session, not from the body (docs/plans/onboarding-harness.md, Phase 3).
   */
  readonly session?: SetupTurnContext;
  /**
   * Setup mode, with files: the thread's newest learned part, carried beside
   * the turn because a long thread's window may leave its message out. The
   * server reads it as it reads the thread.
   */
  readonly learned?: SetupLearned;
  /**
   * The one turn that follows a create-with-voice call. The transcript is the
   * thread; this is the structured note. `screen` is `new_record` or
   * `new_list`, and `origin` here is never `setup`.
   */
  readonly voice?: {
    readonly origin: Extract<LiveOrigin, 'new_record' | 'new_list'>;
    readonly note: RecordNote;
  };
  /**
   * What the person did with the thread's cards, as the phone holds it: at
   * most the last `AGENT_CONTEXT_OUTCOMES_MAX`, each trimmed to its call id,
   * its state and the receipt `cardReceiptOf` derives. It rides with the turn
   * so the next turn never races the outcome PUT; the server merges the stored
   * rows and then these (the phone's win), for ids present in the window only.
   */
  readonly outcomes?: readonly AgentContextOutcome[];
  /**
   * What is on the screen, as data: the list's cut, the rows in view (at most
   * `AGENT_VIEW_ROWS_MAX`), the selection and an open panel
   * (docs/plans/os-live-mode.md, Decided 10). The server re-checks every id
   * against the person's access before the model reads it.
   */
  readonly view?: AgentView;
  /**
   * The web's Report templates pages (D24 of docs/plans/report-templates.md):
   * the record, the template open (null for a new one), the entry the preview
   * shows, and a block the person clicked ("this block: items"). Only a client
   * that sends `doc_templates` sends it; the server re-checks the record.
   */
  readonly document?: {
    readonly recordId: string;
    readonly templateId: string | null;
    readonly entryId: string | null;
    readonly focusBlock: string | null;
  };
};

/** One card's outcome as a turn carries it (`AgentChatContext.outcomes`). */
export type AgentContextOutcome = {
  readonly toolCallId: string;
  readonly state: AgentProposalOutcomeState;
  readonly receipt?: CardReceipt;
};

/** How many card outcomes one turn carries: the newest. */
export const AGENT_CONTEXT_OUTCOMES_MAX = 20;

/**
 * One pinned card, as the phone holds it. `id` is the layout's own hash, which
 * a `card` proposal's `replaces` and a `card_remove` proposal carry back
 * unchanged. `says` is what the card draws in a sentence, for when `label` is
 * a name somebody chose.
 */
export type AgentScreenCard = {
  readonly id: string;
  readonly label: string;
  readonly says?: string;
  readonly card: InsightCardId;
  readonly placement: 'record' | 'dashboard';
  /** The record it is asked of. Absent on a Dashboard card over the whole business. */
  readonly recordId?: string;
  /**
   * The one card the person has open on its own page, or is changing
   * (web-redesign-10 phase 5): "this insight" means it.
   */
  readonly open?: true;
  /**
   * What an open card shows now, in a few words the page drew: its total,
   * its days, the best and the worst (`AGENT_SCREEN_CARD_NUMBERS_MAX` long).
   * Only an open card carries it; the other cards are told by what they ask.
   */
  readonly numbers?: string;
};

/** The longest `numbers` an open card carries. */
export const AGENT_SCREEN_CARD_NUMBERS_MAX = 1200;

/* ------------------------------------------------------------ the threads */

/**
 * One row of GET /agent/threads: a conversation this person had inside this
 * business. docs/plans/agent-thread-history.md. The thread's id is the same
 * `id` `useChat` posts with every turn, minted on the phone.
 */
export type AgentThreadSummary = {
  readonly id: string;
  /** The person's first sentence, trimmed to a line. */
  readonly title: string;
  /** The screen the thread was opened on, in the door's vocabulary (`record`, `team`, ...). */
  readonly screen: string;
  /** The record it was opened in, or null. Null also once that record is gone. */
  readonly recordId: string | null;
  readonly messageCount: number;
  readonly createdAt: string;
  readonly lastMessageAt: string;
};

/** GET /agent/threads?before=<cursor>&limit=20. Newest first; `nextBefore` is null at the end. */
export type AgentThreadListResponse = {
  readonly threads: readonly AgentThreadSummary[];
  readonly nextBefore: string | null;
};

/** A conversation's title is one line on a list row: at most this many characters (the column's check). */
export const AGENT_THREAD_TITLE_MAX = 80;

/**
 * PATCH /agent/threads/:id: the person renames a conversation (the OS page's
 * list, More › Rename). The answer is the thread's `AgentThreadSummary`.
 * DELETE /agent/threads/:id takes nothing and answers 204.
 */
export type AgentThreadRenameRequest = {
  readonly title: string;
};

/**
 * A title the person typed, as it is kept: one line (every run of white space
 * one space), trimmed. Null when nothing is left or it runs past
 * `AGENT_THREAD_TITLE_MAX`, so the web keeps the old title and the server
 * refuses the same words the web would never send.
 */
export const agentThreadTitleOf = (raw: unknown): string | null => {
  if (typeof raw !== 'string') return null;
  const title = raw.replace(/\s+/g, ' ').trim();
  if (title === '' || title.length > AGENT_THREAD_TITLE_MAX) return null;
  return title;
};

/**
 * How a proposal card ended: what the person did, kept beside what the model
 * said and never written into the message. `detail` is the card's own receipt:
 * `ConfirmDone` on the phone for `done` (with `pendingSync: true` while the
 * write still waits in the phone's outbox), how many parts stood and why for a
 * stopped bundle, `{ message }` for `failed`. `failed` (the confirm did not go
 * through) and `edit_first` (the person opened the form with Change it first)
 * leave the card confirmable. The server reads `detail` only to derive a
 * `CardReceipt` for the model.
 */
export type AgentProposalOutcomeState = 'done' | 'undone' | 'left' | 'stopped' | 'failed' | 'edit_first';

/**
 * What the model is told a decided card left behind, derived from the outcome's
 * `detail` (`receiptOf` on the server). The ids are for tools only; the model
 * never shows one. `stood` is how many parts of a bundle stood, `message` the
 * sentence of a failed confirm or a stopped bundle, `pendingSync` a save the
 * phone holds that has not reached the server yet.
 */
export type CardReceipt = {
  readonly entryIds?: readonly string[];
  readonly recordId?: string;
  readonly jobId?: string;
  readonly factId?: string;
  readonly stood?: number;
  readonly message?: string;
  readonly pendingSync?: boolean;
  /** An email card's way (the web's Gmail cards): its emails went as Gmail drafts, or were sent. */
  readonly emailed?: 'drafted' | 'sent';
};

const nonEmpty = (value: unknown): value is string => typeof value === 'string' && value !== '';

/**
 * What the model is told a decided card left behind, read from the outcome
 * row's `detail`. The row keeps what the phone wrote — its `ConfirmDone` for
 * `done` (an `entryId` or `entryIds`, a `recordId`, a `jobId`, a `factId`,
 * `pendingSync` while the write waits in the outbox), `{ stood, message }` for
 * a bundle, `{ message }` for `failed` — and this picks out the parts a later
 * turn can act on. `done` and `stopped` carry what was made; `failed` carries
 * its sentence; `left`, `undone` and `edit_first` leave nothing standing to
 * name, so their receipt is empty. Anything unrecognised is dropped, so a
 * receipt already derived passes through unchanged: the server runs one the
 * phone sent through here again.
 */
export const cardReceiptOf = (state: AgentProposalOutcomeState, detail: unknown): CardReceipt => {
  const held = typeof detail === 'object' && detail !== null && !Array.isArray(detail) ? (detail as Record<string, unknown>) : {};
  if (state === 'failed') return nonEmpty(held.message) ? { message: held.message } : {};
  if (state !== 'done' && state !== 'stopped') return {};
  const entryIds = [
    ...(nonEmpty(held.entryId) ? [held.entryId] : []),
    ...(Array.isArray(held.entryIds) ? held.entryIds.filter(nonEmpty) : []),
  ];
  return {
    ...(entryIds.length > 0 ? { entryIds } : {}),
    ...(nonEmpty(held.recordId) ? { recordId: held.recordId } : {}),
    ...(nonEmpty(held.jobId) ? { jobId: held.jobId } : {}),
    ...(nonEmpty(held.factId) ? { factId: held.factId } : {}),
    ...(typeof held.stood === 'number' && Number.isInteger(held.stood) && held.stood >= 0 ? { stood: held.stood } : {}),
    ...(nonEmpty(held.message) ? { message: held.message } : {}),
    ...(held.pendingSync === true ? { pendingSync: true } : {}),
    ...emailedOf(held),
  };
};

/**
 * An email card's way, from the web's kept `{ mode, results }`, or from a
 * receipt already derived. It is named only when at least one email went that
 * way: a card whose emails all failed, were left out or are still sending
 * carries no way, so the model never says an email went that did not.
 */
const emailedOf = (held: Record<string, unknown>): Pick<CardReceipt, 'emailed'> => {
  if (held.emailed === 'drafted' || held.emailed === 'sent') return { emailed: held.emailed };
  if (!Array.isArray(held.results)) return {};
  const way = held.mode === 'draft' ? 'drafted' : held.mode === 'send' ? 'sent' : null;
  if (way === null) return {};
  const went = held.results.some(
    (result: unknown) => typeof result === 'object' && result !== null && (result as Record<string, unknown>).state === way,
  );
  return went ? { emailed: way } : {};
};

export type AgentProposalOutcome = {
  readonly toolCallId: string;
  readonly state: AgentProposalOutcomeState;
  readonly detail: unknown;
  readonly updatedAt: string;
};

/**
 * GET /agent/threads/:id. The messages are the AI SDK's `UIMessage[]`, typed
 * as such where `ai` is a dependency; this package does not depend on it.
 */
export type AgentThreadResponse = {
  readonly thread: AgentThreadSummary;
  readonly messages: readonly unknown[];
  readonly outcomes: readonly AgentProposalOutcome[];
  /** How the person rated Coach's replies in this thread, one per rated reply. */
  readonly feedback: readonly AgentFeedback[];
};

/** PUT /agent/threads/:id/outcomes/:toolCallId body. */
export type AgentProposalOutcomeRequest = {
  readonly state: AgentProposalOutcomeState;
  readonly detail?: unknown;
};

/* ------------------------------------------------ feedback on a reply */

/**
 * How the person rated one of Coach's replies. There are two ratings and no
 * third: a Report a problem is stored as `down` with `source: 'report'`
 * (A25), because it opens the same sheet (C107) and its after-state looks the
 * same; the source says which door the choice came through.
 */
export type AgentFeedbackRating = 'up' | 'down';

/** Why a reply was wrong: the suffixes of the `agent.feedback.reason.*` keys. */
export const AGENT_FEEDBACK_REASONS = ['numbers', 'understood', 'action', 'other'] as const;
export type AgentFeedbackReason = (typeof AGENT_FEEDBACK_REASONS)[number];

/**
 * Which door the choice came through (A25): `thumbs`, the row's thumbs, or
 * `report`, Report a problem from the row or the reply's long-press. A report
 * is stored as a `down`.
 */
export const AGENT_FEEDBACK_SOURCES = ['thumbs', 'report'] as const;
export type AgentFeedbackSource = (typeof AGENT_FEEDBACK_SOURCES)[number];

/** The longest optional line a down may carry, after trimming. */
export const AGENT_FEEDBACK_NOTE_MAX = 500;

/**
 * One reply's rating, as the thread read hands it back. An `up` is always
 * `source: 'thumbs'` with no reason and no note; a `down` always has a
 * reason, and its source says whether it was a thumbs down or a report.
 */
export type AgentFeedback = {
  readonly messageId: string;
  readonly rating: AgentFeedbackRating;
  readonly source: AgentFeedbackSource;
  readonly reason: AgentFeedbackReason | null;
  readonly note: string | null;
  readonly updatedAt: string;
};

/**
 * PUT /agent/threads/:id/messages/:messageId/feedback body. An up is only
 * ever the thumbs, so it carries no source and is stored as `thumbs`; a down
 * names its door (a report is a `down` with `source: 'report'`), its reason
 * and, optionally, one line.
 */
export type AgentFeedbackRequest =
  | { readonly rating: 'up' }
  | { readonly rating: 'down'; readonly source: AgentFeedbackSource; readonly reason: AgentFeedbackReason; readonly note?: string };

/**
 * The mark on a reply that was small talk: written by the server on the
 * voice's own answers (a thanks, a hello), read by the phone to leave the
 * rating row off, because small talk is never rated (C105).
 */
export const AGENT_SMALL_TALK_METADATA = { smallTalk: true } as const;

/** Whether a message's metadata carries the small-talk mark. */
export const isSmallTalkMetadata = (metadata: unknown): boolean =>
  typeof metadata === 'object' && metadata !== null && (metadata as { smallTalk?: unknown }).smallTalk === true;

/**
 * The mark on a person's line that came through a live call: written by the
 * server on every line a call keeps, read by the web to draw "You said" and
 * the mic over the bubble. A typed turn carries no metadata.
 */
export const AGENT_SPOKEN_METADATA = { spoken: true } as const;

/**
 * The mark on a person's line a live call kept as it was heard, before any
 * request or small talk took it (the 7 Oct fix list, item 1): every line the
 * person's bubble showed is stored, but no answer is owed to one of these.
 */
export const AGENT_HEARD_METADATA = { heard: true } as const;

/** Whether a message's metadata carries the heard mark. */
export const isHeardMetadata = (metadata: unknown): boolean =>
  typeof metadata === 'object' && metadata !== null && (metadata as { heard?: unknown }).heard === true;

/** Whether a message's metadata carries the spoken mark. */
export const isSpokenMetadata = (metadata: unknown): boolean =>
  typeof metadata === 'object' && metadata !== null && (metadata as { spoken?: unknown }).spoken === true;

/**
 * What a line said in live mode is, as its `metadata.voice` says: OS's
 * greeting, an acknowledgement, a fixed line, small talk (`say`), the spoken
 * gist of a reply (which names that reply in `replyTo`), or the person's own
 * words (`user`). Every line spoken in live mode is kept with one, and Coach
 * reads each as said aloud.
 */
export const AGENT_VOICE_KINDS = ['greeting', 'ack', 'say', 'gist', 'fixed', 'user'] as const;
export type AgentVoiceKind = (typeof AGENT_VOICE_KINDS)[number];
/**
 * `spoken`: on the person's line, the language it was said in when the
 * thread writes it in other letters (Devanagari in Roman on a Hinglish app;
 * no line is ever translated, Devendra 12 Oct). A line stored between 7 and
 * 12 Oct may carry it over a translation.
 */
export type AgentVoiceMark = { readonly kind: AgentVoiceKind; readonly replyTo?: string; readonly spoken?: 'en' | 'hi' | 'hi-Latn' };

/** A message's `metadata.voice`, or null when it carries none that reads. */
export const voiceMetadataOf = (metadata: unknown): AgentVoiceMark | null => {
  if (typeof metadata !== 'object' || metadata === null) return null;
  const voice = (metadata as { voice?: unknown }).voice;
  if (typeof voice !== 'object' || voice === null) return null;
  const { kind, replyTo, spoken } = voice as { kind?: unknown; replyTo?: unknown; spoken?: unknown };
  if (typeof kind !== 'string' || !(AGENT_VOICE_KINDS as readonly string[]).includes(kind)) return null;
  return {
    kind: kind as AgentVoiceKind,
    ...(typeof replyTo === 'string' && replyTo !== '' ? { replyTo } : {}),
    ...(spoken === 'en' || spoken === 'hi' || spoken === 'hi-Latn' ? { spoken } : {}),
  };
};

/** The mark on a request a correction replaced: `metadata.replaced.by` names the request that took its place. */
export type AgentReplacedMark = { readonly by: string };

/** A message's `metadata.replaced`, or null. */
export const replacedMetadataOf = (metadata: unknown): AgentReplacedMark | null => {
  if (typeof metadata !== 'object' || metadata === null) return null;
  const replaced = (metadata as { replaced?: unknown }).replaced;
  if (typeof replaced !== 'object' || replaced === null) return null;
  const { by } = replaced as { by?: unknown };
  return typeof by === 'string' && by !== '' ? { by } : null;
};

/**
 * The divider live mode leaves in the thread when it ends: a `system`
 * message with this one part, `{ type: 'data-live-ended', data: { seconds, reason } }`.
 * Coach does not read it.
 */
export const AGENT_LIVE_ENDED_PART = 'data-live-ended';
export type AgentLiveEndedData = { readonly seconds: number; readonly reason: LiveEndReason };
export type AgentLiveEndedPart = { readonly type: typeof AGENT_LIVE_ENDED_PART; readonly data: AgentLiveEndedData };

/** A message's live-ended divider data, or null when it is not one. */
export const liveEndedOf = (message: { readonly role: string; readonly parts: readonly unknown[] }): AgentLiveEndedData | null => {
  if (message.role !== 'system' || message.parts.length !== 1) return null;
  const part = message.parts[0];
  if (typeof part !== 'object' || part === null || (part as { type?: unknown }).type !== AGENT_LIVE_ENDED_PART) return null;
  const data = (part as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const { seconds, reason } = data as { seconds?: unknown; reason?: unknown };
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds < 0 || !isLiveEndReason(reason)) return null;
  return { seconds, reason };
};

/**
 * The server trying a failed business turn again (Decided 8 of
 * docs/plans/files-paging-and-fixes.md): a turn that fails before any of its
 * words, tool calls or data reached the client is started again, up to three
 * attempts in all, and between attempts the turn's own stream carries one
 * transient part, `{ type: 'data-retry', data: { attempt, of }, transient: true }`,
 * `attempt` the one starting now (2 or 3) and `of` the attempts in all. A
 * transient part reaches the chat's `onData` only, never a message, so the
 * thread neither draws nor keeps it; the web's working row says it.
 */
export const AGENT_RETRY_PART = 'data-retry';
export type AgentRetryData = { readonly attempt: number; readonly of: number };

/** A stream part's retry, or null when it is not one that reads. */
export const agentRetryOf = (part: unknown): AgentRetryData | null => {
  if (typeof part !== 'object' || part === null || (part as { type?: unknown }).type !== AGENT_RETRY_PART) return null;
  const data = (part as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const { attempt, of } = data as { attempt?: unknown; of?: unknown };
  if (typeof attempt !== 'number' || typeof of !== 'number' || !Number.isInteger(attempt) || !Number.isInteger(of)) return null;
  return attempt >= 1 && attempt <= of ? { attempt, of } : null;
};

/**
 * The divider live mode leaves when it carries on after a setup's Create
 * (step 5.2): a `system` message with this one part,
 * `{ type: 'data-live-now-in', data: { name } }`, drawn "Now in <name>."
 * (`agent.live.setup.now_in`). Coach does not read it.
 */
export const AGENT_LIVE_NOW_IN_PART = 'data-live-now-in';
export type AgentLiveNowInData = { readonly name: string };

/** A message's now-in divider data, or null when it is not one. */
export const liveNowInOf = (message: { readonly role: string; readonly parts: readonly unknown[] }): AgentLiveNowInData | null => {
  if (message.role !== 'system' || message.parts.length !== 1) return null;
  const part = message.parts[0];
  if (typeof part !== 'object' || part === null || (part as { type?: unknown }).type !== AGENT_LIVE_NOW_IN_PART) return null;
  const data = (part as { data?: unknown }).data;
  if (typeof data !== 'object' || data === null) return null;
  const { name } = data as { name?: unknown };
  return typeof name === 'string' && name.trim() !== '' ? { name } : null;
};

/* ---------------------------------------------------- how a reply ended */

/**
 * How a kept reply of Coach's ended short, as its `metadata.outcome` says:
 * `stopped` when the person stopped the turn and the server kept what Coach
 * had, `failed` when the turn failed and the server kept its failed step.
 * A reply that finished carries neither.
 */
export type AgentReplyOutcome = 'stopped' | 'failed';

/** A message's `metadata.outcome`, read loosely: null for anything but the two outcomes. */
export const agentReplyOutcomeOf = (metadata: unknown): AgentReplyOutcome | null => {
  if (typeof metadata !== 'object' || metadata === null) return null;
  const outcome = (metadata as { readonly outcome?: unknown }).outcome;
  return outcome === 'stopped' || outcome === 'failed' ? outcome : null;
};

/* -------------------------------------------------------------- the facts */

/**
 * One thing the person has told the assistant that the records cannot hold,
 * in their words (decision 13 of docs/plans/agent-business-memory.md). A
 * `business` fact is read by every member's assistant; a `person` fact by its
 * person's alone. A correction never edits: it inserts a successor carrying
 * `supersedes` and retires the old one, so `retiredAt` is set exactly when
 * `status` is `retired`.
 */
export type BusinessFact = {
  readonly id: string;
  readonly scope: 'business' | 'person';
  readonly text: string;
  /** What it is filed under: a record, a directory row, or a member. Null when it is about the business as a whole. */
  readonly about: { readonly recordId?: string; readonly entryId?: string; readonly userId?: string } | null;
  readonly status: 'active' | 'retired';
  /** The fact this one corrected, or null. */
  readonly supersedes: string | null;
  /**
   * setup: a line of the onboarding understanding. chat: a confirmed card.
   * screen: the What the assistant knows screen. file: read in a file the
   * person gave OS (docs/plans/setup-from-files.md, Decided 20).
   */
  readonly source: 'setup' | 'chat' | 'screen' | 'file';
  /** The file a `file` fact was read in; null for every other source, and once that file is removed. */
  readonly sourceFileId: string | null;
  readonly createdAt: string;
  readonly retiredAt: string | null;
};

/**
 * POST /v1/facts. Refused `FACT_NOT_ALLOWED` (403) for a `business` fact from
 * someone without `manage_policies`, and `FACT_SIMILAR` (409, with the match)
 * when an active fact reads nearly the same and `force` is not true. The
 * screen's editor sends `force` after showing the person the like fact; the
 * agent re-proposes as a correction instead.
 */
export type FactCreateRequest = {
  readonly text: string;
  readonly scope: BusinessFact['scope'];
  readonly about?: BusinessFact['about'];
  readonly source: 'chat' | 'screen';
  readonly sourceThreadId?: string;
  readonly sourceMessageId?: string;
  readonly force?: boolean;
};

/**
 * PUT /v1/facts/:id: a correction (`text` given) or a forget (`text` absent).
 * Both retire `:id`; a correction inserts the successor with `supersedes` set,
 * in the same transaction, and answers it. 404 `FACT_NOT_FOUND`, 409
 * `FACT_RETIRED` when `:id` no longer holds.
 */
export type FactChangeRequest = {
  readonly text?: string;
  readonly about?: BusinessFact['about'];
  readonly source: 'chat' | 'screen';
  readonly sourceThreadId?: string;
  readonly sourceMessageId?: string;
  readonly force?: boolean;
};

/** GET /v1/facts?status=active|retired|all. RLS decides what this person sees. */
export type FactListResponse = { readonly facts: readonly BusinessFact[] };

/* -------------------------------------------------------------- the recall */

/**
 * One thing recall surfaced (decisions 5, 6 and 15 of
 * docs/plans/agent-business-memory.md). Two kinds, told apart by `kind`:
 *
 *   exchange   a past message and its neighbour: the person's line (`said`)
 *              and the reply it got (`answered`), each trimmed to 300
 *              characters, with the day, the screen, the record it was said
 *              in, and what the person did with the card the reply drew, when
 *              it drew one.
 *   fact       a fact whose words match, active or retired: past the forty
 *              the brief carries whole, this is how the rest are found, and a
 *              retired one is how "what did I say about samples" is answered
 *              by a sentence that no longer holds.
 *
 * `day` is yyyy-mm-dd in the person's zone. `score` is the trigram
 * similarity plus the full-text rank, best first; it orders and is never shown.
 */
export type AgentRecall =
  | {
      readonly kind: 'exchange';
      readonly threadId: string;
      readonly day: string;
      readonly screen: string;
      /** The record the thread was opened in, by name, or null on a screen that is no record. */
      readonly recordName: string | null;
      readonly said: string;
      readonly answered: string | null;
      readonly outcome: AgentProposalOutcomeState | null;
      readonly score: number;
    }
  | {
      readonly kind: 'fact';
      readonly factId: string;
      readonly day: string;
      readonly said: string;
      readonly scope: BusinessFact['scope'];
      readonly status: BusinessFact['status'];
      /** The day it was retired, in the person's zone, or null while it holds. */
      readonly retiredDay: string | null;
      readonly score: number;
    };

/**
 * One past card, for the brief's *Recently proposed* line (decision 6): what
 * kind of card, its one-line summary, the day it was drawn, and what the
 * person did with it. `outcome` is null when the phone never said.
 */
export type AgentRecentProposal = {
  readonly day: string;
  readonly kind: string;
  readonly summary: string;
  readonly outcome: AgentProposalOutcomeState | null;
};

/**
 * The 409 `FACT_SIMILAR` body: the `ApiError` shape with the standing fact
 * beside it, so the screen can show the person the like fact and the agent
 * can re-propose as a change to it.
 */
export type FactSimilarError = {
  readonly error: { readonly code: 'FACT_SIMILAR'; readonly message: string; readonly fact: BusinessFact };
};

/* ---------------------------------------------------------- the proposals */

/**
 * One reason a proposal could not stand. `fieldId` is null when it is not
 * about one field. `path` is set on a setup refusal, where there is no field
 * id yet: it names the record and field in the draft, the way the template
 * validator does (`composed.records[visits].fields[fee]`).
 */
export type AgentProblem = {
  readonly code: string;
  readonly fieldId: string | null;
  readonly message: string;
  readonly path?: string;
  /**
   * What the model should do next. The phone draws `message`, never this.
   * Set on OPTION_UNKNOWN and REFERENCE_UNKNOWN so a missing choice or list
   * row becomes a create-card, not a question.
   */
  readonly fix?: string;
};

/**
 * What the phone sends after the person confirms a missing-value create, so
 * the agent continues the original entry without asking them to repeat it.
 */
export const AGENT_CONTINUE_AFTER_SAVE =
  'That is saved. Continue with the rest of what I asked. Do not ask me to repeat it.';

/**
 * One entry, validated exactly as POST /records/:id/entries validates before
 * it inserts. `values` is keyed by field id, which is what `useEntries().create`
 * takes; `fields` repeats the same values with the words a card can show.
 */
export type EntryProposal = {
  readonly type: 'entry';
  readonly recordId: string;
  readonly recordName: string;
  /**
   * The visible fields this entry leaves empty, by label. For the assistant
   * only: the card draws the fields it holds, so a blank is not on the card.
   * A client that draws the card ignores it.
   */
  readonly blank?: readonly string[];
  /** List vs record. The card names a list in the header and the tap; a record keeps the entry noun. */
  readonly kind: VisibleRecordKind;
  readonly values: Readonly<Record<string, string | number | boolean | readonly string[] | null>>;
  readonly fields: ReadonlyArray<{
    readonly fieldId: string;
    readonly label: string;
    readonly value: string | number | boolean | readonly string[] | null;
    /** The value as a person reads it: a directory row's title, an option's word. */
    readonly display: string | null;
    /**
     * A multi select's rows by name, in the order they were chosen, for a card
     * that draws them as chips. Absent on every other field, and on a card
     * proposed before it existed: `display` holds the same names joined.
     */
    readonly chips?: readonly string[];
    /**
     * The parent row a filtered reference's row is filed under, by name: the
     * card says "Electricity, under Utilities". Absent on every other field.
     */
    readonly under?: string;
  }>;
  /**
   * Each row the values name, by its own title, for a form opened from the
   * card (Edit first): `display` may say more than the title ("Utilities ·
   * from Sub-category"). Absent on a card proposed before it existed.
   */
  readonly titles?: Readonly<Record<string, string>>;
  readonly preview: {
    readonly title: string | null;
    /** The signed amount the record's total would move by. Null when it measures nothing. */
    readonly amount: number | null;
    readonly currency: string | null;
    /** Formula answers, keyed by label. A hop that needs another row says so as null. */
    readonly computed: Readonly<Record<string, string | number | boolean | null>>;
  };
  /**
   * The entry's lines, in the same write (Decision 5 of
   * docs/plans/entry-lines-and-layout.md): typed values only, keyed by the
   * `lines` field id, a room line holding its guest lines. Exactly the
   * `lines` that `useEntries().create` takes. Absent on an entry with none.
   */
  readonly lines?: EntryLinesWrite;
  /** The same lines in the words a card draws, each with its worked-out answers. Absent with `lines`. */
  readonly lineRuns?: readonly AgentLineRun[];
};

/**
 * One line on an entry card: what it is called, and every value it holds as
 * a person reads it, a worked-out one included ("Amount: 4,000"). A depth-1
 * line carries its own lines in `runs`.
 */
export type AgentLine = {
  readonly entryId: string;
  /** The line's name: the title of the row its first reference names, else its first value. */
  readonly title: string | null;
  readonly fields: ReadonlyArray<AgentLineField>;
  readonly runs?: readonly AgentLineRun[];
  /**
   * The line works an amount out and it came out blank, an input it reads
   * being missing (a room's Nights with no Check out): the card puts no amount
   * on the right, never a typed one (its Rate) in its place. Absent when the
   * amount answered, or the line works none out.
   */
  readonly amountBlank?: true;
};

/**
 * One value of a line on a card. `display` is the server's word for it; a
 * plain number or amount also carries `number`, so the card formats it as
 * the form does (`lineCardFace`): an amount in the record's or the
 * business's currency, a number grouped. `workedOut` marks a line formula's
 * answer, which the card may put on the right as the line's amount.
 */
export type AgentLineField = {
  readonly label: string;
  readonly display: string;
  readonly number?: number;
  readonly money?: boolean;
  readonly workedOut?: boolean;
};

/** The lines of one `lines` field, as a card draws them. */
export type AgentLineRun = { readonly fieldId: string; readonly label: string; readonly lines: readonly AgentLine[] };

/**
 * What a line holds beyond its title, in one line, for a card: "Rate: 2000 ·
 * Amount: 4000". The field the title came from is said once, as the title.
 */
export const agentLineSummary = (line: AgentLine): string => {
  let titled = false;
  return line.fields
    .filter((field) => {
      if (titled || line.title === null || field.display !== line.title) return true;
      titled = true;
      return false;
    })
    .map((field) => `${field.label}: ${field.display}`)
    .join(' · ');
};

/** How many lines a set of runs holds, at both levels. */
export const agentLineCount = (runs: readonly AgentLineRun[] | undefined): number =>
  (runs ?? []).reduce((sum, run) => sum + run.lines.reduce((inner, line) => inner + 1 + agentLineCount(line.runs), 0), 0);

/**
 * The lines of a card with every line id made again, at both levels, so each
 * tap writes lines of its own: the device names a line's id (Decision 5), and
 * a card confirmed twice, or a retry after a write that did land, must not
 * name the lines of the first.
 */
export const withFreshLineIds = (lines: EntryLinesWrite, mint: () => string): EntryLinesWrite => {
  const out: Record<string, readonly LineWrite[]> = {};
  for (const [fieldId, run] of Object.entries(lines)) {
    out[fieldId] = run.map((line) => ({
      entryId: mint(),
      values: line.values,
      ...(line.lines === undefined ? {} : { lines: withFreshLineIds(line.lines, mint) }),
    }));
  }
  return out;
};

/**
 * The title of each row an entry card names, for the form Edit first opens,
 * so its picker shows the row's own name. The card's `titles` win; a card
 * proposed before them parks a reference by what it showed, and a multi
 * select by its chips, row for row.
 */
export const entryProposalTitles = (proposal: EntryProposal): Record<string, string> => {
  const titles: Record<string, string> = {};
  for (const field of proposal.fields) {
    if (typeof field.value === 'string' && field.display !== null) titles[field.value] = field.display;
    const chips = field.chips;
    if (Array.isArray(field.value) && chips !== undefined && chips.length === field.value.length) {
      field.value.forEach((rowId, at) => {
        titles[rowId] = chips[at]!;
      });
    }
  }
  return { ...titles, ...(proposal.titles ?? {}) };
};

/**
 * Several entries of one record at once: the rows of a new list, or a day's
 * sales read off a slip. Each one is an `EntryProposal` that stood on its own;
 * the phone writes them in order through the same `useEntries().create` a
 * single entry goes through, and the card names every one.
 */
export type EntriesProposal = {
  readonly type: 'entries';
  readonly recordId: string;
  readonly recordName: string;
  readonly kind: VisibleRecordKind;
  readonly entries: readonly EntryProposal[];
};

/**
 * A structure change, planned by `planStructure` against the record as it
 * stands. `request` is the exact body `PATCH /records/:id/structure` takes, so
 * the phone posts it through `useStructure().save` unchanged.
 */
export type StructureProposal = {
  readonly type: 'structure';
  readonly recordId: string;
  readonly recordName: string;
  readonly fieldSetVer: number;
  readonly request: RecordStructureRequest;
  /**
   * When the change is to the fields of each LINE (`linesField`), the lines
   * fields from the root down to this lines record, each with the word one of
   * its lines goes by when the owner set one: the card says "On each line of
   * Rooms", or "Guests, inside each room". Absent on a record's own fields.
   */
  readonly linesPath?: ReadonlyArray<{ readonly label: string; readonly noun: string | null }>;
  readonly summary: ReadonlyArray<{
    readonly change: 'add' | 'update' | 'archive' | 'rename' | 'order';
    readonly label: string;
    readonly detail: string;
  }>;
};

/** A new link, checked by the same rules `POST /records/:id/links` applies. */
export type LinkProposal = {
  readonly type: 'link';
  readonly fromRecordId: string;
  readonly fromRecordName: string;
  readonly toRecordId: string;
  readonly toRecordName: string;
  readonly request: LinkCreateRequest;
  readonly summary: string;
  /**
   * Set only on a link part of a bundle whose from or to is a record an
   * earlier part of the same card creates (Decided L2 of
   * docs/plans/links-fixes.md). That record has no id yet, so its id and the
   * ids of its fields and choices stand as placeholders in `fromRecordId` or
   * `toRecordId` and in `request`; the tap fills them in from the record it
   * just made (`fillPendingLink`) before it posts the link.
   */
  readonly pendingRecord?: PendingLinkRecord;
};

/** The record a bundled link leans on, and how each placeholder in the link is found on it once it is made. */
export type PendingLinkRecord = {
  /** The record's name, as the record part of the same card names it. */
  readonly name: string;
  /** Which end of the link it is. */
  readonly side: 'from' | 'to';
  /** The id standing in for the record. */
  readonly placeholderId: string;
  /** Each field placeholder, by the label the record will give it. */
  readonly fields: ReadonlyArray<{ readonly id: string; readonly label: string }>;
  /** Each choice placeholder, by its field's label and its own word or key. */
  readonly options: ReadonlyArray<{
    readonly id: string;
    readonly fieldLabel: string;
    readonly label: string | null;
    readonly labelKey: string | null;
  }>;
};

/** A change to a link the record already holds. `patch` is the PATCH body. */
/**
 * A change to a stored link, or its removal. `patch` is the PATCH body; when
 * `remove` is true there is no patch, the tap is the DELETE, and the rows the
 * link already wrote stay where they are (the route keeps them).
 */
export type LinkChangeProposal = {
  readonly type: 'link_change';
  readonly recordId: string;
  readonly linkId: string;
  readonly toRecordName: string;
  readonly patch: LinkPatchRequest;
  readonly remove?: boolean;
  readonly summary: string;
};

/**
 * An edit to one live entry, or its removal. `values` is the PATCH body's
 * `values`: only the fields that change, keyed by field id, with null to
 * clear one. `fields` says each change as a person reads it.
 */
export type EntryChangeProposal = {
  readonly type: 'entry_change';
  readonly recordId: string;
  readonly recordName: string;
  readonly entryId: string;
  /** The entry's title before the change, for the card's header. */
  readonly title: string | null;
  readonly change:
    | {
        readonly kind: 'edit';
        readonly values: Readonly<Record<string, string | number | boolean | readonly string[] | null>>;
        readonly fields: ReadonlyArray<{
          readonly fieldId: string;
          readonly label: string;
          readonly before: string | null;
          readonly after: string | null;
        }>;
        /**
         * The full set of each `lines` field the change names (Decision 5):
         * the lines it keeps as they are, the one it changes, without the one
         * it removes. The PATCH body's `lines`. Absent when no line changes.
         */
        readonly lines?: EntryLinesWrite;
        /**
         * The entry's version when `lines` was read. Present with `lines`, and
         * sent as the PATCH's `baseVersion`: a card confirmed after the entry
         * moved is refused, so its full set never removes a line added since.
         */
        readonly baseVersion?: number;
        /** Each line it adds, changes or removes, as the card says it. */
        readonly lineChanges?: readonly AgentLineChange[];
      }
    | { readonly kind: 'delete' };
};

/**
 * One line an entry change touches, in words: where it sits ("Rooms" or
 * "Rooms › Room 101 › Guests"), and the line before and after it. `before` is
 * null on a line added, `after` on one removed.
 */
export type AgentLineChange = {
  readonly change: 'add' | 'change' | 'remove';
  readonly run: string;
  readonly before: string | null;
  readonly after: string | null;
};

/** The business's own name, mark or default currency: the PATCH /businesses/:id body. */
export type BusinessProposal = {
  readonly type: 'business';
  readonly patch: BusinessPatchRequest;
  readonly summary: readonly string[];
};

/**
 * One thing to remember, correct or forget (decision 14 of
 * docs/plans/agent-business-memory.md). Three cards from one shape, told
 * apart by `action`:
 *
 *   remember   *Remember: "text"*, with the scope under it. `request` is the
 *              POST /v1/facts body; `factId` and `replaces` are null.
 *   replace    *Replaces: old → new*. `replaces` is the standing fact, `text`
 *              the successor's words; `request` is the PUT /v1/facts/:id body
 *              and `factId` the `:id`.
 *   forget     *Forget: old*. `text` is null and so is `request.text`; the
 *              same PUT retires `:id` and answers 204.
 *
 * On confirm the phone posts `request` verbatim to the route `action` names.
 * It may add `sourceThreadId` and `sourceMessageId`, which it holds and the
 * tool does not. `about` is already ids: the tool resolved the names the model
 * gave. `summary` is the one line the card and the reply share.
 */
export type FactProposal = {
  readonly type: 'fact';
  readonly action: 'remember' | 'replace' | 'forget';
  /** The sentence to keep, in the person's words. Null on a forget. */
  readonly text: string | null;
  readonly scope: BusinessFact['scope'];
  readonly about: BusinessFact['about'];
  /** The fact this corrects or forgets, with its words for the card. Null on a remember. */
  readonly replaces: { readonly id: string; readonly text: string } | null;
  readonly request: FactCreateRequest | FactChangeRequest;
  /** The `:id` of PUT /v1/facts/:id. Null on a remember, which is a POST. */
  readonly factId: string | null;
  readonly summary: readonly string[];
};

/**
 * One insight card, in the tile vocabulary the device's `insight-layout.ts`
 * parses (`expr` over field ids, `where` over field ids). `label` is a literal
 * word the person chose, never a catalogue key.
 *
 * Everything below `expr` came with "one formula everywhere". The tile now
 * says what it counts three ways, and exactly one of them is set:
 *
 *   expr     the string grammar every card written before this shape used.
 *            Still written whenever the card can be said in it, so a phone
 *            that has not learnt the rest still draws the card.
 *   measure  one total over one row source: the record's own measure, one
 *            field, or a row formula worked out per row and then added up.
 *   combine  an aggregate tree whose leaves are totals, possibly in another
 *            record: what each customer owes is deliveries minus payments.
 *            This is the same tree `InsightAggNode` names everywhere else;
 *            the device's own measure carries it as `{ agg: 'formula', expr }`.
 *
 * A card whose combine reaches another record cannot live on that record's own
 * summary, so `placement` says where it goes and the phone confirms it through
 * the matching hook. Absent means the record's own summary, which is where
 * every card proposed before this shape went.
 */
export type CardProposal = {
  readonly type: 'card';
  readonly recordId: string;
  readonly recordName: string;
  readonly card: InsightCardId;
  /** Absent means the record's own summary. */
  readonly placement?: 'record' | 'dashboard';
  /**
   * The pinned card this one takes the place of, on the same strip: its
   * layout id, and the name it wore for the card to say. Set when the person
   * asked to change a card rather than add one: the phone swaps it in where
   * the old one stood instead of adding it at the end.
   */
  readonly replaces?: { readonly id: string; readonly label: string };
  readonly tile: {
    readonly label: string;
    readonly expr?: string;
    readonly measure?:
      | { readonly agg: 'sum' | 'avg' | 'min' | 'max' | 'count'; readonly of: InsightRowSource }
      | { readonly agg: 'rows' };
    readonly combine?: InsightAggNode;
    readonly scope?: 'day' | 'week' | 'month' | 'year' | 'all';
    readonly group_by?: string;
    /** A reference on the record; then `group_by` names a field on the row it points at. */
    readonly group_via?: string;
    /** The same, for the group an `argmax` ranks by. */
    readonly leader_via?: string;
    readonly days?: number;
    readonly limit?: number;
    readonly where?: ReadonlyArray<{
      readonly fieldId: string;
      readonly op: InsightFilterOp;
      /** A list on `in` and `not_in` ("is any of", "is none of"), one value otherwise. */
      readonly value?: InsightWhere['value'];
      /** A reference on the record; then `fieldId` is a field on the row it points at. */
      readonly via?: string;
    }>;
  };
  readonly summary: string;
};

/**
 * A pinned card taken down. `cardId` is the phone's own layout id, straight
 * from the `cards` it sent in the chat context; the phone removes it from the
 * strip `placement` names. Nothing is computed or lost: a card is a question,
 * and the entries it counted stay.
 */
/**
 * One fixed choice to turn into a list. Nothing changes until the person
 * confirms: the confirm sends POST /records/:recordId/fields/:fieldId/to-list,
 * which creates the list from the choices' words, moves every entry's choice to
 * its row, and changes the field into a reference to that list.
 */
export type FieldConvertProposal = {
  readonly type: 'field_convert';
  readonly recordId: string;
  readonly recordName: string;
  readonly fieldId: string;
  readonly fieldLabel: string;
  /** The name the new list takes. */
  readonly listName: string;
  /** The choices, in order. Each becomes one row of the list. */
  readonly words: readonly string[];
};

/**
 * One record or list to remove. Nothing is removed until the person confirms:
 * the confirm sends DELETE /records/:recordId, the same route the screen uses,
 * which needs the caller may make records. The card names the record and says
 * that the removal is the person's to confirm.
 */
export type RecordRemoveProposal = {
  readonly type: 'record_remove';
  readonly recordId: string;
  readonly recordName: string;
  readonly kind: VisibleRecordKind;
};

export type CardRemoveProposal = {
  readonly type: 'card_remove';
  readonly cardId: string;
  readonly label: string;
  readonly placement: 'record' | 'dashboard';
  /** The record whose strip holds it. Null for a Dashboard card over the whole business. */
  readonly recordId: string | null;
  readonly recordName: string | null;
};

/**
 * An invitation. The route mints a token and never sends anything, so the
 * phone shares it with `phoneOrEmail` itself; the value is carried for that
 * and for the card's wording only. No records ride here: `InviteCreateRequest`
 * takes a level and a job, and the job is what opens records on arrival.
 */
export type InviteProposal = {
  readonly type: 'invite';
  readonly phoneOrEmail: string;
  /**
   * Set only for a client that draws roles (it sends the `roles` feature,
   * web-redesign-02 phase 5): the invite as `POST /v1/invites` takes it, with
   * a role, what they can open and Reports to. The client posts `request`
   * rather than the old level-and-job invite; the old fields above stay filled
   * so the card reads the same. `request.roleId` is null when the role is one
   * an earlier part of the same bundle makes: the tap fills it in from that
   * part, as it does for a job.
   */
  readonly person?: InvitePersonPart;
  /**
   * What the person called them, when they said a name. The route takes none
   * — an invitation is minted against a phone or an email — so this is for the
   * card alone, which is the difference between "9876543210" and "Kavya".
   */
  readonly name?: string;
  readonly authority: Authority;
  readonly roleId: string | null;
  readonly jobName: string | null;
};

/** What an invite carries for a client that draws roles: the new invite, and its words. */
export type InvitePersonPart = {
  readonly request: Omit<PersonInviteRequest, 'roleId'> & { readonly roleId: string | null };
  readonly roleName: string;
  /** The records and lists it opens, by name, for the card. */
  readonly opens: readonly AccessProposalRecord[];
  /** Who they will report to, by name; null when nobody. */
  readonly reportsToName: string | null;
};

/**
 * Several invitations in one card. Each one stood on its own against the same
 * ladder a single invitation climbs, and the phone mints them in order through
 * the same `useMembers().invite`, stopping at the first that is refused.
 * `summary` says how many and at which levels; the rows say who.
 */
export type InvitesProposal = {
  readonly type: 'invites';
  readonly invites: readonly InviteProposal[];
  readonly summary: string;
};

/**
 * A whole business, composed in setup mode before any tenant exists. `setup`
 * is exactly the body `POST /onboarding/compose` takes beside a `clientKey`,
 * already past `composeProblems`; the phone posts it when the person confirms.
 */
export type SetupProposal = {
  readonly type: 'setup';
  readonly setup: ComposedSetup;
  /**
   * What the build noted it could not hold (Decision 18 of
   * docs/plans/entry-lines-and-layout.md), the same list `setup.unmetNeeds`
   * carries to the route. Here for whoever reads the proposal (the evals, a
   * log); `setup` is what the phone posts, so the route reads it there. Never
   * drawn on the card.
   */
  readonly unmetNeeds?: readonly ComposedUnmetNeed[];
};

/**
 * A new record or list, made the way POST /records makes one: one the
 * person named, with the first fields the agent planned for it. The phone
 * creates it and then applies `request`.
 */
export type RecordProposal = {
  readonly type: 'record';
  readonly source?: 'blank';
  readonly blank: RecordBlankSpec;
  readonly request: RecordStructureRequest | null;
  readonly summary: ReadonlyArray<{
    readonly label: string;
    readonly detail: string;
  }>;
};

/* ------------------------------------------------------------- the team */

/**
 * A new job, checked the way POST /businesses/:id/jobs checks it: the spec
 * vocabulary, and no capability above the level that could hold the job.
 * `request` is the exact body that route takes. `summary` says each pattern in
 * words: which records, what they may do, what is hidden.
 */
export type JobProposal = {
  readonly type: 'job';
  readonly request: JobCreateRequest;
  readonly summary: readonly string[];
};

/**
 * A change to a job this business owns. `patch` is the PATCH /jobs/:id body.
 * `reachesHolders` is true when the spec changes, because that reaches every
 * person holding the job the next time it is applied, and the card must say so.
 */
export type JobChangeProposal = {
  readonly type: 'job_change';
  readonly jobId: string;
  readonly jobName: string;
  readonly patch: JobPatchRequest;
  readonly holders: number;
  readonly reachesHolders: boolean;
  readonly summary: readonly string[];
};

/**
 * Give a job to people who are already in the business. `userIds` is the
 * POST /jobs/:id/apply body; `memberNames` are the words the card shows.
 */
export type JobApplyProposal = {
  readonly type: 'job_apply';
  readonly jobId: string;
  readonly jobName: string;
  readonly userIds: readonly string[];
  readonly memberNames: readonly string[];
};

/**
 * One person's key to one record, changed by hand or taken back. A patch is
 * the PATCH /records/:id/grants/:userId body and marks the grant customised,
 * as every hand edit does; `revoke` is the DELETE. `summary` says the change
 * in words, field labels rather than ids.
 */
export type GrantProposal = {
  readonly type: 'grant';
  readonly recordId: string;
  readonly recordName: string;
  readonly userId: string;
  readonly memberName: string;
  readonly change: { readonly kind: 'patch'; readonly patch: GrantPatchRequest } | { readonly kind: 'revoke' };
  readonly summary: string;
};

/**
 * One person's level changed, or the person taken out of the business. The
 * level change is `PATCH /businesses/:id/members/:userId` with `{ authority }`;
 * the removal is the DELETE, which keeps every entry they wrote with their name
 * on it. `from` is the level they hold today, so a confirmed change can be put
 * back. `closesAsk` is true when the change makes them a primary admin and they
 * had an open ask for exactly that: the server closes the ask in the same
 * transaction, and the card says so.
 */
export type MemberChangeProposal = {
  readonly type: 'member_change';
  readonly userId: string;
  readonly memberName: string;
  readonly from: Authority;
  readonly change: { readonly kind: 'level'; readonly authority: Authority } | { readonly kind: 'remove' };
  readonly summary: string;
  readonly closesAsk?: boolean;
};

/**
 * An ask to become a primary admin, or one put away. `ask` is the caller's own
 * hand going up (`POST /businesses/:id/primary-requests`, admin or partner
 * only); `dismiss` is a primary admin putting somebody's open ask away
 * (`DELETE .../primary-requests/:userId`). Granting an ask is not here: it is a
 * `member_change` to the primary level, or, where the app draws roles, the
 * primary admin's hand-over from Team (a workspace has one primary admin).
 */
export type PrimaryRequestProposal = {
  readonly type: 'primary_request';
  readonly change:
    | { readonly kind: 'ask' }
    | {
        readonly kind: 'dismiss';
        readonly userId: string;
        readonly memberName: string;
        /** The open ask `POST /v1/asks/:id/answer` puts away; set when `roles` is. */
        readonly askId?: string;
      };
  /**
   * Confirmed through the /v1 routes of a client that draws roles (roles-parity
   * 2.10): `POST /v1/workspace/primary/ask` from anyone but the primary admin,
   * and `POST /v1/asks/:id/answer` to put one away. Absent, the old
   * primary-requests routes.
   */
  readonly roles?: true;
  readonly summary: string;
};

/* ---------------------------------------------------------- roles and access */

/*
 * The four kinds below are offered only to a client that says it draws roles
 * (`x-alluzo-client` carries the `roles` feature, web-redesign-02 phase 5).
 * A phone already in people's hands never receives one; OS tells it to update
 * the app instead. Each carries the exact body of the /v1 route it confirms
 * through (phase 4), already checked the way that route checks it.
 */

/** A new role: `POST /v1/roles`. `summary` says each permission that is on, in words. */
export type RoleProposal = {
  readonly type: 'role';
  readonly request: RoleCreateRequest;
  readonly summary: readonly string[];
};

/**
 * A change to a role: `PATCH /v1/roles/:id`. `patch.permissions`, when
 * present, is the whole set. `holders` is how many people hold the role, and
 * `lowered` what goes quiet for each of them (Decided 6): kept, not deleted.
 */
export type RoleChangeProposal = {
  readonly type: 'role_change';
  readonly roleId: string;
  readonly roleName: string;
  readonly patch: RolePatchRequest;
  readonly holders: number;
  readonly lowered: readonly PermissionMove[];
  readonly raised: readonly PermissionMove[];
  readonly summary: readonly string[];
};

/**
 * One person given a role (`PATCH /v1/members/:userId` with `{ roleId }`), or
 * taken out of the workspace (`DELETE /v1/members/:userId`), which keeps every
 * entry they wrote and moves their team up to their manager.
 */
export type RoleGiveProposal = {
  readonly type: 'role_give';
  readonly userId: string;
  readonly memberName: string;
  readonly fromRoleId: string | null;
  readonly fromRoleName: string;
  /**
   * Whether the role they held is within the caller's own, so giving it back
   * would pass the route's ceiling: Undo is offered only then. Absent from a
   * server before roles-parity phase 2, which offers no Undo.
   */
  readonly withinOwn?: boolean;
  /**
   * A removal aimed at the caller: leaving. Set only then; absent from a
   * server before the roles-parity phase-3 review, whose leave card reads as
   * a removal.
   */
  readonly self?: true;
  readonly change:
    | { readonly kind: 'role'; readonly roleId: string; readonly roleName: string }
    | { readonly kind: 'remove'; readonly teamSize: number; readonly teamMovesToName: string | null };
  readonly summary: string;
};

/**
 * The primary admin hands the workspace over (roles-parity 3.6): `POST
 * /v1/workspace/primary/handover` with `request`, the person and the role the
 * primary admin keeps. Nothing moves until that person accepts; then, in one
 * transaction, they become the primary admin and the caller takes the role.
 */
export type HandoverProposal = {
  readonly type: 'handover';
  readonly request: PrimaryHandoverRequest;
  readonly memberName: string;
  readonly keepRoleName: string;
  readonly summary: string;
};

/**
 * A hand-over waiting to be accepted, answered (roles-parity 8.1). `accept`
 * is `POST /v1/workspace/primary/handover/accept` with `{ askId }`, by the
 * person it waits on (an offer made since on other terms is refused):
 * in one transaction they become the primary admin and the one who offered
 * it takes the role they kept. `decline` (by that person) and `withdraw` (by
 * the primary admin who offered it) are `POST /v1/asks/:askId/answer` with
 * `{ answer: 'dismiss' }`. Nothing moves until the person confirms.
 */
export type HandoverAnswerProposal = {
  readonly type: 'handover_answer';
  readonly askId: string;
  readonly answer: 'accept' | 'decline' | 'withdraw';
  /** The other person in it: who offered it (accept, decline), or who it waits on (withdraw). */
  readonly memberName: string;
  /** The role the one who offered it keeps once it is accepted. */
  readonly keepRoleName: string;
  readonly summary: string;
};

/**
 * An open invitation withdrawn (`DELETE /v1/invites/:inviteId`) or sent again
 * (`POST /v1/invites/:inviteId/resend`: a fresh code, seven more days),
 * roles-parity 3.5. A withdrawn invitation's link and code stop working.
 */
export type InviteChangeProposal = {
  readonly type: 'invite_change';
  readonly inviteId: string;
  readonly change: 'withdraw' | 'resend';
  /** Who it was sent to, for the card. */
  readonly name: string;
  readonly phoneOrEmail: string;
  readonly roleName: string;
  readonly summary: string;
};

/**
 * An ask for a right (roles-parity 3.4): `POST /v1/asks` with `request`, from
 * anyone. It reaches everyone who may change a person's role and access.
 * `what` is the right in words, for the card.
 */
export type AskProposal = {
  readonly type: 'ask';
  readonly request: AskCreateRequest;
  readonly what: string;
  readonly summary: string;
};

/**
 * An open ask for a right answered (roles-parity 3.4): `POST
 * /v1/asks/:askId/answer` with `request`. Granting a permission gives the
 * person `request.roleId`; granting a record gives them that access.
 */
export type AskAnswerProposal = {
  readonly type: 'ask_answer';
  readonly askId: string;
  readonly userId: string;
  readonly memberName: string;
  readonly request: AskAnswerRequest;
  /** The right asked for, in words. */
  readonly what: string;
  /** The role a granted permission comes with, by name. */
  readonly roleName?: string;
  readonly summary: string;
};

/**
 * A role removed (roles-parity 3.3): `DELETE /v1/roles/:id?moveTo=<moveToId>`.
 * Its people, and the open invitations that carry it, move to `moveToId`; the
 * role is archived. `undo` is what putting it back needs, as the web's remove
 * Undo does it: the role made again (`POST /v1/roles`) and given back to
 * `holderIds`. `allHoldersRead` is false when the people read were fewer than
 * the role's holders, so an Undo could not give it back to all of them.
 */
export type RoleRemoveProposal = {
  readonly type: 'role_remove';
  readonly roleId: string;
  readonly roleName: string;
  readonly moveToId: string;
  readonly moveToName: string;
  readonly holders: number;
  readonly undo: {
    readonly name: string;
    readonly purpose: string | null;
    readonly permissions: RolePermissions;
    readonly holderIds: readonly string[];
    readonly allHoldersRead: boolean;
  };
  readonly summary: readonly string[];
};

/** A made role with the same permissions as another: `POST /v1/roles/:id/copy` with `request`. */
export type RoleCopyProposal = {
  readonly type: 'role_copy';
  readonly roleId: string;
  readonly roleName: string;
  /** The copy's name: `request.name`, or what the route names it, "<role> (copy)". */
  readonly name: string;
  readonly request: RoleCopyRequest;
  readonly summary: readonly string[];
};

/**
 * One person's Reports to, details or role, changed together (roles-parity
 * 3.1, 3.2): `PATCH /v1/members/:userId` with `request`, which names only what
 * moves. `before` is what they held, so an Undo sends it back through the same
 * route; `undoable` is whether that would pass the route (the role they held
 * within the caller's own, and the old Reports to one the caller may set).
 */
export type PersonChangeProposal = {
  readonly type: 'person_change';
  readonly userId: string;
  readonly memberName: string;
  /** The caller changing themselves: their own name, phone or address. */
  readonly self: boolean;
  readonly request: PersonPatchRequest;
  readonly before: {
    readonly roleId: string | null;
    readonly roleName: string;
    readonly reportsTo: string | null;
    readonly reportsToName: string | null;
    readonly name: string | null;
    readonly phone: string | null;
    readonly address: string | null;
  };
  /** The new manager's name when Reports to moves; null for nobody. */
  readonly reportsToName?: string | null;
  /** The new role's name when the role moves. */
  readonly roleName?: string;
  readonly undoable: boolean;
  readonly summary: readonly string[];
};

/** One record or list in an access card, by name: what it is set to, and each field and list set on its own. */
export type AccessProposalRecord = {
  readonly recordId: string;
  readonly recordName: string;
  readonly access: RecordAccessLevel | 'none';
  /** What they held before, for the card's "from"; null for an invite. */
  readonly before?: RecordAccessLevel | 'none';
  readonly fields: ReadonlyArray<{ readonly fieldId: string; readonly name: string; readonly access: AccessLevel }>;
  readonly lists: ReadonlyArray<{ readonly recordId: string; readonly name: string; readonly access: AccessLevel }>;
};

/**
 * Access given, changed or taken for one person: `PUT /v1/members/:userId/access`
 * with `request`. It can give a record from nothing, with fields and related
 * lists; `access: 'none'` takes a record away.
 */
export type AccessProposal = {
  readonly type: 'access';
  readonly userId: string;
  readonly memberName: string;
  readonly request: PersonAccessPutRequest;
  readonly records: readonly AccessProposalRecord[];
  /**
   * Whether putting the card back would pass the route: false when a record it
   * moves was quiet (stored above what the role allows now), since the PUT
   * refuses to store that again (ABOVE_ROLE). Undo is offered only when true.
   * Absent from a server before roles-parity phase 2, which leaves Undo as it was.
   */
  readonly undoable?: boolean;
  readonly summary: string;
};

/** The access set one record of an access card writes, as the route reads it. */
export type AccessProposalSet = AccessSet & { readonly recordId: string };

/**
 * Several proposals the person confirms in one tap: a job and the invitations
 * that carry it, names into two lists, a field and the card that totals it.
 *
 * Each part is a proposal that already stood on its own, made by the tool that
 * owns it, so the phone confirms it through the hook that type already uses.
 * They run in the card's order and stop at the first refusal, because a part
 * may lean on the one before it: an invitation naming a job this same card
 * creates carries the job's NAME and a null `roleId`, and the tap fills the id
 * in from the job it just made.
 *
 * A bundle never holds another bundle: the parts are the tools' own outputs
 * and no tool answers one.
 */
export type BundleProposal = {
  readonly type: 'bundle';
  readonly parts: readonly AgentProposal[];
  readonly summary: string;
};

export type Finding = {
  readonly rank: number;
  readonly title: string;
  readonly why: string;
  readonly expert: 'links' | 'fields' | 'records' | 'insights' | null;
  readonly recordName: string | null;
};

export type FindingsOutput = {
  readonly kind: 'findings';
  readonly summary: string;
  readonly items: readonly Finding[];
};

export type AgentProposal =
  | EntryProposal
  | EntriesProposal
  | StructureProposal
  | LinkProposal
  | LinkChangeProposal
  | CardProposal
  | CardRemoveProposal
  | RecordRemoveProposal
  | FieldConvertProposal
  | InviteProposal
  | InvitesProposal
  | BundleProposal
  | SetupProposal
  | RecordProposal
  | JobProposal
  | JobChangeProposal
  | JobApplyProposal
  | GrantProposal
  | MemberChangeProposal
  | PrimaryRequestProposal
  | EntryChangeProposal
  | BusinessProposal
  | FactProposal;

/**
 * The kinds only a client that draws roles is sent (web-redesign-02 phase 5).
 * They are kept out of `AgentProposal` on purpose: that union is what every
 * shipped client draws, and its switches are exhaustive. Stage 3 (the web) and
 * stage 4 (the phone) fold these into it once they draw them.
 */
export type RoleAgentProposal = RoleProposal | RoleChangeProposal | RoleGiveProposal | AccessProposal | PersonChangeProposal | RoleRemoveProposal | RoleCopyProposal | AskProposal | AskAnswerProposal | InviteChangeProposal | HandoverProposal | HandoverAnswerProposal;

/**
 * The role kinds a client that draws roles reads as cards: the four of
 * web-redesign-02, and the kinds OS's phase-3 tools send (roles-parity 3.1
 * to 3.6, and 8.1's hand-over answer), which the phone and the web draw since
 * roles-parity 3.7.
 */
export const ROLE_PROPOSAL_TYPES = [
  'role',
  'role_change',
  'role_give',
  'access',
  'person_change',
  'role_remove',
  'role_copy',
  'ask',
  'ask_answer',
  'invite_change',
  'handover',
  'handover_answer',
] as const satisfies readonly RoleAgentProposal['type'][];

/** A bundle as a client that draws roles is sent it: its parts may be role kinds. */
export type RoleBundleProposal = Omit<BundleProposal, 'parts'> & { readonly parts: readonly WideAgentProposal[] };

/** Every kind the server may send: `AgentProposal` to every client, and the role kinds only to a client that draws roles. */
export type WideAgentProposal = AgentProposal | RoleAgentProposal | RoleBundleProposal;

/* ------------------------------------------------------- the web-only kinds */

/**
 * One row of a business detail card (D21 of docs/plans/report-templates.md):
 * a value to save under a key, the value it replaces, and where it was heard.
 * `value: null` asks: the card draws an empty input for the person to type.
 * `slot` is null for a single-valued key, or for a new value of a multi-valued
 * one (the route picks a free slot).
 */
export type BusinessDetailProposalRow = {
  readonly key: string;
  readonly slot: string | null;
  readonly value: BusinessDetailValue | null;
  /** The active value this row replaces, or null when the key holds none. */
  readonly was: BusinessDetailValue | null;
  readonly from: 'person' | 'file' | 'website';
  readonly fileId: string | null;
};

/**
 * Business details OS offers to save (`propose_business_detail`, D21): a GSTIN
 * said in the chat with the state and PAN it gives, or the details a document
 * still needs, asked as empty rows. Save posts the rows to
 * `POST /v1/businesses/:id/details`; Undo puts back what they replaced.
 */
export type BusinessDetailProposal = {
  readonly type: 'business_detail';
  readonly rows: readonly BusinessDetailProposalRow[];
  /** The Phase 1 keys the business still lacks once these are saved. */
  readonly missingAfter: readonly string[];
  readonly summary: string;
};

/**
 * What a draft's dry run found, in words (D15, D16 of
 * docs/plans/report-templates.md): the model reads these and never a figure.
 */
export type DocTemplateFacts = {
  /** The total check on the sample entry: `match`, `rounding`, `mismatch` or `not_checked`. */
  readonly total: DocTotalWord;
  /** Why a total does not match: `price_basis`, `discount_missing`, `line_amounts`, `unknown`, or null. */
  readonly totalCause: DocTotalCause;
  /**
   * What the record's formulas say about its prices (D15): `exclusive`,
   * `inclusive` or `unknown`; `ask` when the seat should ask "Do your prices
   * include GST?" with two previews. `from` and `fieldSetVer` say who
   * decided the layout's price basis and against which `field_set_ver`: the
   * formulas, the person's answer, or nobody yet. The card keeps them, so a
   * later edit (and Save) keeps the person's answer and does not ask again.
   */
  readonly basis: {
    readonly read: 'exclusive' | 'inclusive' | 'unknown';
    readonly ask: boolean;
    readonly from: 'formula' | 'person' | null;
    readonly fieldSetVer: number | null;
  };
  /** Where the place of supply comes from on the sample entry, or null when no GST is printed. */
  readonly placeOfSupplyFrom: 'override' | 'ship_to' | 'buyer' | 'buyer_gstin' | 'business' | null;
  /** Bind keys the author's own copy leaves out. */
  readonly hidden: readonly string[];
  /** What issue would refuse for the sample entry (the e-invoice rule, the HSN digits). */
  readonly warnings: readonly DocEntryRefusal[];
};

/**
 * A report template OS drafts or changes (`draft_template`, `edit_template`;
 * D23 of docs/plans/report-templates.md and proposal.md 4.4 of
 * docs/report-templates-study). Web only. The layout rides in wire form,
 * fields by label, as the model wrote it; Save turns it into ids against the
 * live record and the route judges it again.
 */
export type DocTemplateProposal = {
  readonly type: 'doc_template';
  readonly recordId: string;
  readonly recordName: string;
  /** Set when the draft changes a saved template; null for a new one. */
  readonly templateId: string | null;
  /** The saved version the draft changes, for the save's stale check; null for a new template. */
  readonly baseVersion: number | null;
  readonly name: string;
  readonly kind: DocKind;
  readonly layout: DocLayoutWire;
  /** The blocks in order, in one line, for the model: "head, title, meta, bill, items, tax, sum, words". */
  readonly outline: string;
  /** The kind's checklist: Save works with gaps, Issue waits for them. */
  readonly gaps: readonly DocGap[];
  /** The business detail keys a gap asks for (`gstin`), for the details card. */
  readonly missingDetails: readonly string[];
  readonly facts: DocTemplateFacts;
  /** Block ids this proposal changed; every block for a first draft. */
  readonly changed: readonly string[];
  /** The entry the dry run read, or null when it printed sample values. */
  readonly sampleEntryId: string | null;
  /** Sample values (D5): the record has no entry to show. */
  readonly sample: boolean;
  /** What the layout reads: the record's field ids and the business detail keys (D18). */
  readonly uses: { readonly fields: readonly string[]; readonly details: readonly string[] };
  /** The number series it is numbered from: one on file, or the one Save makes. Null for an unnumbered kind. */
  readonly series: {
    readonly id: string | null;
    readonly name: string;
    /** The number the next document would print, made by code. */
    readonly example: string;
    readonly exists: boolean;
  } | null;
  /** One line per change, joined, for the card and the model. */
  readonly summary: string;
};

/**
 * The kinds only the web draws (D1, D23). Kept out of `WideAgentProposal`,
 * whose switches on the phone are exhaustive; the server sends these only to
 * a client that says `doc_templates` or `business_details`. They never sit
 * inside a bundle.
 */
export type DocAgentProposal = DocTemplateProposal | BusinessDetailProposal;

/** Every kind the server may send to the web: a superset of `WideAgentProposal`. */
export type WebAgentProposal = WideAgentProposal | DocAgentProposal;

export const DOC_PROPOSAL_TYPES = ['doc_template', 'business_detail'] as const satisfies readonly DocAgentProposal['type'][];

/** Whether a proposal is one of the web-only kinds, which never sit inside a bundle. */
export const isDocProposal = (proposal: WebAgentProposal): proposal is DocAgentProposal => (DOC_PROPOSAL_TYPES as readonly string[]).includes(proposal.type);

/** Whether a proposal is a kind the phone may be sent: anything but the web-only kinds. */
export const isWideProposal = (proposal: WebAgentProposal): proposal is WideAgentProposal => !isDocProposal(proposal);

/**
 * Whether confirming this card takes something away: an entry removed, an
 * insight taken down, a link removed, a person removed, a key taken back, a
 * fact forgotten, or a bundle holding any of these. The voice may read such
 * a card back, but only a tap acts on it (Decided S20): the phone raises the
 * touch confirmation and the server tells the voice which cards are which.
 *
 * It reads defensively, because the server hands it stored proposals whose
 * shape was never checked: a missing `change` or `parts` is not destructive.
 */
export const isDestructiveProposal = (proposal: WebAgentProposal): boolean => {
  switch (proposal.type) {
    case 'entry_change':
      return proposal.change?.kind === 'delete';
    case 'card_remove':
      return true;
    case 'record_remove':
      return true;
    case 'field_convert':
      return true;
    case 'link_change':
      return proposal.remove === true;
    case 'member_change':
      return proposal.change?.kind === 'remove';
    case 'grant':
      return proposal.change?.kind === 'revoke';
    case 'role_give':
      return proposal.change?.kind === 'remove';
    case 'role_remove':
      return true;
    case 'invite_change':
      return proposal.change === 'withdraw';
    case 'access':
      return Array.isArray(proposal.records) && proposal.records.some((one) => one?.access === 'none');
    case 'fact':
      return proposal.action === 'forget';
    case 'business_detail':
      return false;
    case 'doc_template':
      return false;
    case 'bundle':
      return Array.isArray(proposal.parts) && proposal.parts.some((part) => typeof part === 'object' && part !== null && isDestructiveProposal(part));
    default:
      return false;
  }
};

/* ------------------------------------------------------------ the envelope */

export type ProposalOutput = {
  readonly kind: 'proposal';
  readonly proposal: AgentProposal;
  /** Always empty on a proposal: a problem makes it a refusal instead. */
  readonly problems: readonly [];
  /** One line of what follows once this is confirmed. The phone draws it as "Coming next". */
  readonly next?: string;
  /** Things the person should hear beside the card: a check that did not run, a row that was dropped. */
  readonly notes?: readonly string[];
  /**
   * The existing record a link into or out of this card's new record will
   * join, once the record is saved (R8 of docs/plans/qa-fixes-2.md). A link
   * needs both records to exist, so OS proposes the record alone and the
   * reply ends on a line saying to ask again for the link. No client draws it.
   */
  readonly linkAfter?: string;
  /**
   * The draft the card also is, for the live preview beside an AI-first page
   * (`draftOf`, web-redesign-10 Decided 3): set on a record, fields, link,
   * insight or role proposal, absent on every other kind.
   */
  readonly draft?: AgentDraft;
};

export type RefusedOutput = {
  readonly kind: 'refused';
  readonly code: string;
  readonly message: string;
  readonly problems: readonly AgentProblem[];
  /**
   * On a fence, the record at a level or the permission the person lacks, when
   * the tool can name it: the card's Ask an admin sends it as an ask
   * (`POST /v1/asks`). Absent, the card opens Team instead.
   */
  readonly right?: AskCreateRequest['right'];
};

export type QuestionOutput = {
  readonly kind: 'question';
  readonly question: string;
  readonly options: readonly string[];
  /** How many chips may be picked. `many` sends the ticked set as one line. Absent is one. */
  readonly pick?: 'one' | 'many';
  /** One line the person reads under the question: what their answer changes. */
  readonly why?: string;
  /** While setting up: the understanding slot the question fills. Never drawn. */
  readonly slot?: string;
  /** Who raised it when a specialist did (records, fields, links, insights). The phone draws a tag. */
  readonly from?: string;
  /**
   * The documents seat's price basis question (D15 of
   * docs/plans/report-templates.md): the web's preview pane shows one real
   * entry printed both ways beside it. Web only; the phone never receives it.
   */
  readonly previews?: 'price_basis';
};

/**
 * The gatherer's one part per turn: what it has noted so far, how much of
 * what this kind of business needs that covers, and the question it asks
 * next, if any. The phone draws the understanding as "What I have so far",
 * the question as chips (typing) or speaks it (a call), and lights Build my
 * setup when `ready`. docs/plans/done/onboarding-two-phases.md, decision 1.
 */
export type UnderstandingOutput = {
  readonly kind: 'understanding';
  readonly understanding: SetupUnderstanding;
  readonly coverage: Coverage;
  /** The next question, or absent when the gatherer asked nothing this turn. */
  readonly ask?: QuestionOutput;
  /** Coverage is complete, or the gatherer chose to stop: the phone says so and the person builds. */
  readonly ready: boolean;
  /** The slot of the question just answered, when the note left it open. Kept for the phone's "Just noted" diff. */
  readonly unrecorded?: Slot;
};

export const BUILD_STAGES = ['records', 'fields', 'links', 'cards', 'check'] as const;
export type BuildStage = (typeof BUILD_STAGES)[number];

export type BuildCounts = {
  readonly records: number;
  readonly lists: number;
  readonly fields: number;
  readonly formulas: number;
  readonly links: number;
  readonly cards: number;
};

/**
 * The names the build has drafted so far (docs/plans/business-map-onboarding-web.md,
 * 2.3): the records and lists once they are named, and each link, by the
 * names of the records it joins, once the links are wired. The web's lessons
 * and its sketch say these while it builds; the phone does not read them.
 */
export type BuildNames = {
  readonly records: readonly string[];
  readonly lists: readonly string[];
  /** Absent until the links stage has run. */
  readonly links?: ReadonlyArray<{ readonly from: string; readonly to: string }>;
};

/** What the build has done and is doing, streamed as it runs. The phone draws the rows. */
export type ProgressOutput = {
  readonly kind: 'progress';
  readonly stage: BuildStage;
  readonly done: readonly BuildStage[];
  readonly counts: BuildCounts;
  readonly line: string;
  /** What the stages before this one named; absent on the first part. Optional, so the phone is unchanged. */
  readonly names?: BuildNames;
};

export type ErrorOutput = {
  readonly kind: 'error';
  readonly error: { readonly code: string; readonly message: string };
};

/** A read's answer. The facts vary per tool; `kind` is what stays the same. */
export type AnswerOutput = { readonly kind: 'answer' } & Readonly<Record<string, unknown>>;

/**
 * One way to narrow a large read, offered as a chip. Exactly one of the
 * three narrowings rides beside the label: a `window` the read takes as
 * `from`/`to`, a `groupBy` field id for a grouped total, or `top` for the N
 * largest groups. A suggestion with none of them is "a name": the person
 * types one and the read takes it as `search`.
 */
export type AgentLargeSuggestion = {
  readonly label: string;
  readonly window?: { readonly from: string; readonly to: string };
  readonly groupBy?: string;
  readonly top?: number;
};

/**
 * The one read that answers before reading (decision 9 of
 * docs/plans/agent-business-memory.md). `list_entries` and a grouped `total`
 * count first; past the threshold, with no window and no search on the call,
 * they answer this instead of rows: how many rows there are, the span of
 * their days, and the narrowings the phone can draw as chips. `groups` rides
 * on a grouped total: how many distinct groups the field holds. The model
 * asks once with `ask_user`, the suggestions' labels as the options and
 * "All of it" last; the phone need not know this kind at all, because the
 * question it draws is an ordinary `ask_user`.
 */
export type AgentLargeRead = {
  readonly kind: 'large';
  readonly recordName: string;
  readonly rows: number;
  readonly groups?: number;
  readonly span: { readonly first: string | null; readonly last: string | null };
  readonly suggestions: ReadonlyArray<AgentLargeSuggestion>;
  /** One line for the model: what this is and what to do with it. */
  readonly note: string;
};

/** A proposal envelope carrying any kind the server may send; see `WideAgentProposal`. */
export type WideProposalOutput = Omit<ProposalOutput, 'proposal'> & { readonly proposal: WideAgentProposal };

/** What a server tool answers: `AgentToolOutput`, with a proposal of any kind the server may send. */
export type WideAgentToolOutput = Exclude<AgentToolOutput, ProposalOutput> | WideProposalOutput;

/** A proposal envelope carrying any kind the server may send to the web; see `WebAgentProposal`. */
export type WebProposalOutput = Omit<ProposalOutput, 'proposal'> & { readonly proposal: WebAgentProposal };

/** What a server tool answers, with a proposal of any kind the web draws. */
export type WebAgentToolOutput = Exclude<AgentToolOutput, ProposalOutput> | WebProposalOutput;

export type AgentToolOutput =
  | AnswerOutput
  | AgentLargeRead
  | ProposalOutput
  | RefusedOutput
  | QuestionOutput
  | UnderstandingOutput
  | ProgressOutput
  | ErrorOutput
  | FindingsOutput
  | ViewOutput
  | FillOutput;
